import Footer from "./Footer";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/spacem8-logo.png";
import {
  Bell,
  BrainCircuit,
  Building2,
  CalendarDays,
  ChartNoAxesCombined,
  ChevronDown,
  ChevronRight,
  FileText,
  House,
  LogOut,
  Menu,
  MessageSquareMore,
  Moon,
  ReceiptText,
  Search,
  Settings,
  Sun,
  Target,
  User,
  UserPlus,
  Users,
  Wallet,
  Wrench,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { canAccessPath, canManageUsers } from "../lib/permissions";
import { supabase } from "../lib/supabase";

const topNavItems = [
  { label: "Dashboard", icon: House, path: "/" },
  { label: "Properties", icon: Building2, path: "/properties" },
  { label: "Tenants", icon: Users, path: "/tenants" },
];

const lowerNavItems = [
  { label: "AI Assistant", icon: BrainCircuit, path: "/ai-assistant" },
  { label: "Outreach Agent", icon: Target, path: "/outreach-agent" },
  { label: "User Management", icon: UserPlus, path: "/user-management" },
  { label: "Settings", icon: Settings, path: "/settings" },
];

const tenantPortalItems = [
  { label: "Home", icon: House, path: "/" },
  { label: "Invoices", icon: FileText, path: "/tenant/invoices" },
  { label: "Payments", icon: Wallet, path: "/tenant/payments" },
  { label: "Documents", icon: FileText, path: "/tenant/documents" },
  { label: "Requests", icon: Wrench, path: "/tenant/requests" },
];

const navGroups = [
  {
    label: "Finance",
    icon: Wallet,
    items: [
      { label: "Payments", icon: Wallet, path: "/payments" },
      { label: "Expenses", icon: ReceiptText, path: "/expenses" },
      { label: "Invoices", icon: FileText, path: "/invoices" },
    ],
  },
  {
    label: "Operations Hub",
    icon: MessageSquareMore,
    items: [
      { label: "Maintenance", icon: Wrench, path: "/maintenance" },
      { label: "Vendors", icon: Users, path: "/vendors" },
      { label: "Messages", icon: MessageSquareMore, path: "/messages" },
      { label: "Documents", icon: FileText, path: "/documents" },
      { label: "Calendar", icon: CalendarDays, path: "/calendar" },
      { label: "Reports", icon: ChartNoAxesCombined, path: "/reports" },
    ],
  },
];

const aiShortcutPrompts = [
  "Summarize rent collection performance this month",
  "Which property needs urgent attention this week?",
  "Draft a payment reminder for pending tenants",
  "What should I prioritize today across the portfolio?",
];

function TopButton({
  children,
  className = "",
  onClick,
  type = "button",
  title,
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      title={title}
      className={`inline-flex h-10 items-center justify-center rounded-xl border px-3 text-sm font-medium transition ${className}`}
    >
      {children}
    </button>
  );
}

function SearchInput({ isDark }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const runSearch = async (value) => {
    const nextQuery = value.trim();
    setQuery(value);

    if (nextQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    setIsOpen(true);

    const role = profile?.role;
    const searchText = nextQuery.toLowerCase();
    const requests = [];

    if (canAccessPath(role, "/properties")) {
      requests.push(
        supabase
          .from("properties")
          .select("id, name, location, type")
          .limit(8)
          .then(({ data, error }) => ({
            type: "Property",
            path: "/properties",
            data: error
              ? []
              : (data || []).filter((item) =>
                  `${item.name} ${item.location} ${item.type}`.toLowerCase().includes(searchText)
                ),
          }))
      );
    }

    if (canAccessPath(role, "/tenants")) {
      requests.push(
        supabase
          .from("tenants")
          .select("id, full_name, property_name, unit")
          .limit(12)
          .then(({ data, error }) => ({
            type: "Tenant",
            path: "/tenants",
            data: error
              ? []
              : (data || []).filter((item) =>
                  `${item.full_name} ${item.property_name} ${item.unit}`.toLowerCase().includes(searchText)
                ),
          }))
      );
    }

    if (canAccessPath(role, "/invoices")) {
      requests.push(
        supabase
          .from("invoices")
          .select("id, tenant, property, unit, status")
          .limit(12)
          .then(({ data, error }) => ({
            type: "Invoice",
            path: "/invoices",
            data: error
              ? []
              : (data || []).filter((item) =>
                  `${item.id} ${item.tenant} ${item.property} ${item.unit} ${item.status}`.toLowerCase().includes(searchText)
                ),
          }))
      );
    }

    if (canAccessPath(role, "/payments")) {
      requests.push(
        supabase
          .from("payments")
          .select("id, tenant_name, property_name, unit, invoice_id, status")
          .limit(12)
          .then(({ data, error }) => ({
            type: "Payment",
            path: "/payments",
            data: error
              ? []
              : (data || []).filter((item) =>
                  `${item.tenant_name} ${item.property_name} ${item.unit} ${item.invoice_id} ${item.status}`.toLowerCase().includes(searchText)
                ),
          }))
      );
    }

    if (canAccessPath(role, "/expenses")) {
      requests.push(
        supabase
          .from("expenses")
          .select("id, property_name, category, vendor, status")
          .limit(12)
          .then(({ data, error }) => ({
            type: "Expense",
            path: "/expenses",
            data: error
              ? []
              : (data || []).filter((item) =>
                  `${item.id} ${item.property_name} ${item.category} ${item.vendor} ${item.status}`.toLowerCase().includes(searchText)
                ),
          }))
      );
    }

    if (canAccessPath(role, "/maintenance")) {
      requests.push(
        supabase
          .from("maintenance_tickets")
          .select("id, issue, property, unit, status, priority")
          .limit(12)
          .then(({ data, error }) => ({
            type: "Ticket",
            path: "/maintenance",
            data: error
              ? []
              : (data || []).filter((item) =>
                  `${item.id} ${item.issue} ${item.property} ${item.unit} ${item.status} ${item.priority}`.toLowerCase().includes(searchText)
                ),
          }))
      );
    }

    if (canAccessPath(role, "/vendors")) {
      requests.push(
        supabase
          .from("vendors")
          .select("id, name, company, service_category, phone, email, status")
          .limit(12)
          .then(({ data, error }) => ({
            type: "Vendor",
            path: "/vendors",
            data: error
              ? []
              : (data || []).filter((item) =>
                  `${item.name} ${item.company} ${item.service_category} ${item.phone} ${item.email} ${item.status}`.toLowerCase().includes(searchText)
                ),
          }))
      );
    }

    if (canAccessPath(role, "/documents")) {
      requests.push(
        supabase
          .from("tenants")
          .select("id, full_name, property_name, supporting_documents")
          .limit(12)
          .then(({ data, error }) => ({
            type: "Document",
            path: "/documents",
            data: error
              ? []
              : (data || []).filter((item) =>
                  `${item.full_name} ${item.property_name} ${item.supporting_documents}`.toLowerCase().includes(searchText)
                ),
          }))
      );
    }

    const groupedResults = await Promise.all(requests);
    const flattenedResults = groupedResults.flatMap((group) =>
      group.data.map((item) => {
        const title =
          item.name ||
          item.full_name ||
          item.tenant ||
          item.tenant_name ||
          item.issue ||
          item.id;
        const detail =
          item.location ||
          item.property_name ||
          item.property ||
          item.invoice_id ||
          item.status ||
          "";

        return {
          id: `${group.type}-${item.id}`,
          type: group.type,
          title,
          detail,
          path: group.path,
        };
      })
    );

    setResults(flattenedResults.slice(0, 10));
    setLoading(false);
  };

  return (
    <div className="relative w-[180px] transition-all duration-300 ease-out focus-within:w-[320px] md:w-[220px] md:focus-within:w-[380px]">
      <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <input
        value={query}
        onChange={(event) => runSearch(event.target.value)}
        onFocus={() => {
          if (results.length > 0 || query.trim().length >= 2) setIsOpen(true);
        }}
        placeholder="Search properties, tenants, reports..."
        className={`h-10 w-full rounded-xl border pl-10 pr-3 text-xs outline-none transition-all duration-300 focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
          isDark
            ? "border-slate-700 bg-slate-900 text-white placeholder:text-slate-400"
            : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
        }`}
      />

      {isOpen ? (
        <div
          className={`fixed right-6 top-24 z-[9998] w-80 rounded-2xl border p-3 shadow-xl ${
            isDark
              ? "border-slate-700 bg-slate-900 text-white"
              : "border-slate-200 bg-white text-slate-900"
          }`}
        >
          <div className="flex items-center justify-between px-1 pb-3">
            <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Search results
            </p>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className={`text-xs font-semibold ${isDark ? "text-slate-400" : "text-slate-500"}`}
            >
              Close
            </button>
          </div>

          <div className="max-h-96 space-y-2 overflow-y-auto">
            {loading ? (
              <div className={`rounded-xl p-4 text-sm ${isDark ? "bg-slate-950 text-slate-300" : "bg-slate-50 text-slate-600"}`}>
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div className={`rounded-xl p-4 text-sm ${isDark ? "bg-slate-950 text-slate-300" : "bg-slate-50 text-slate-600"}`}>
                No matching records found.
              </div>
            ) : (
              results.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setQuery("");
                    navigate(item.path);
                  }}
                  className={`w-full rounded-xl border p-3 text-left transition ${
                    isDark
                      ? "border-slate-800 bg-slate-950 hover:bg-slate-800"
                      : "border-slate-200 bg-slate-50 hover:bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item.title}
                    </p>
                    <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                      {item.type}
                    </span>
                  </div>
                  <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {item.detail}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ProfileMenu({ isDark }) {
  const { profile, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef(null);
  const displayName = profile?.full_name || user?.email?.split("@")[0] || "SpaceM8 User";
  const role = profile?.role || "Viewer";
  const email = profile?.email || user?.email || "No email";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative flex items-center gap-1"
      onMouseEnter={() => {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
        setIsOpen(true);
      }}
      onMouseLeave={() => {
        closeTimeoutRef.current = setTimeout(() => {
          setIsOpen(false);
        }, 180);
      }}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        title="Account menu"
        className={`inline-flex h-10 items-center gap-2 rounded-xl border px-2.5 transition ${
          isDark
            ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        }`}
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-orange-500 text-xs font-semibold text-white">
          {initials || "SM"}
        </div>

        <div className="hidden text-left lg:block">
          <p className="max-w-[110px] truncate text-xs font-semibold leading-none">{displayName}</p>
          <p className={`mt-1 text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {role}
          </p>
        </div>

        <ChevronDown
          className={`hidden h-4 w-4 transition lg:block ${
            isOpen ? "rotate-180" : ""
          } ${isDark ? "text-slate-400" : "text-slate-500"}`}
        />
      </button>

      {isOpen ? (
        <div
          role="menu"
          onMouseEnter={() => {
            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
            }
            setIsOpen(true);
          }}
          onMouseLeave={() => {
            closeTimeoutRef.current = setTimeout(() => {
              setIsOpen(false);
            }, 180);
          }}
          className={`fixed right-6 top-24 z-[9999] w-72 rounded-2xl border p-3 shadow-xl ${
            isDark
              ? "border-slate-700 bg-slate-900 text-white"
              : "border-slate-200 bg-white text-slate-900"
          }`}
        >
          <div
            className={`rounded-xl border p-4 ${
              isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-orange-500 text-sm font-semibold text-white">
                {initials || "SM"}
              </div>
              <div className="min-w-0">
                <p className={`truncate text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {displayName}
                </p>
                <p className={`truncate text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {email}
                </p>
              </div>
            </div>
            <div className="mt-3 inline-flex rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
              {role}
            </div>
          </div>

          <div className="mt-3 space-y-2">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                navigate("/settings");
              }}
              className={`flex h-11 w-full items-center justify-between rounded-xl px-3 text-left text-sm font-medium transition ${
                isDark ? "hover:bg-slate-800" : "hover:bg-slate-50"
              }`}
            >
              My profile
              <User className="h-4 w-4 text-slate-400" />
            </button>

            {canManageUsers(role) ? (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/user-management");
                }}
                className={`flex h-11 w-full items-center justify-between rounded-xl px-3 text-left text-sm font-medium transition ${
                  isDark ? "hover:bg-slate-800" : "hover:bg-slate-50"
                }`}
              >
                User Management
                <Settings className="h-4 w-4 text-slate-400" />
              </button>
            ) : null}

            <button
              type="button"
              onClick={async () => {
                setIsOpen(false);
                await signOut();
              }}
              className={`flex h-11 w-full items-center justify-between rounded-xl px-3 text-left text-sm font-medium transition ${
                isDark ? "text-rose-300 hover:bg-slate-800" : "text-rose-700 hover:bg-rose-50"
              }`}
            >
              Sign out
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildNotificationItem(type, title, detail, path) {
  return {
    id: `${type}-${title}-${detail}`,
    type,
    title,
    detail,
    path,
  };
}

function NotificationsMenu({ isDark }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const closeTimeoutRef = useRef(null);

  const fetchNotifications = async () => {
    setLoading(true);
    const role = profile?.role;
    const today = new Date();
    const todayText = today.toISOString().slice(0, 10);
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    const leaseWindowEnd = thirtyDaysFromNow.toISOString().slice(0, 10);
    const nextItems = [];

    const requests = [];

    if (canAccessPath(role, "/invoices")) {
      requests.push(
        supabase
          .from("invoices")
          .select("id, tenant, property, due_date, amount, status")
          .in("status", ["Outstanding", "Partial", "Overdue"])
          .limit(8)
      );
    } else {
      requests.push(Promise.resolve({ data: [], error: null }));
    }

    if (canAccessPath(role, "/payments")) {
      requests.push(
        supabase
          .from("payments")
          .select("id, tenant_name, property_name, due_date, amount, status")
          .in("status", ["Pending", "Overdue"])
          .limit(8)
      );
    } else {
      requests.push(Promise.resolve({ data: [], error: null }));
    }

    if (canAccessPath(role, "/maintenance")) {
      requests.push(
        supabase
          .from("maintenance_tickets")
          .select("id, issue, property, priority, status")
          .in("priority", ["High", "Urgent"])
          .not("status", "eq", "Closed")
          .limit(8)
      );
    } else {
      requests.push(Promise.resolve({ data: [], error: null }));
    }

    if (canAccessPath(role, "/tenants")) {
      requests.push(
        supabase
          .from("tenants")
          .select("id, full_name, property_name, lease_end, status")
          .gte("lease_end", todayText)
          .lte("lease_end", leaseWindowEnd)
          .limit(8)
      );
    } else {
      requests.push(Promise.resolve({ data: [], error: null }));
    }

    const [invoiceResult, paymentResult, maintenanceResult, leaseResult] =
      await Promise.all(requests);

    (invoiceResult.data || []).forEach((invoice) => {
      nextItems.push(
        buildNotificationItem(
          "Invoice",
          `${invoice.status} invoice ${invoice.id}`,
          `${invoice.tenant} - ${invoice.property} - due ${invoice.due_date}`,
          "/invoices"
        )
      );
    });

    (paymentResult.data || []).forEach((payment) => {
      nextItems.push(
        buildNotificationItem(
          "Payment",
          `${payment.status} payment`,
          `${payment.tenant_name} - ${payment.property_name} - due ${payment.due_date}`,
          "/payments"
        )
      );
    });

    (maintenanceResult.data || []).forEach((ticket) => {
      nextItems.push(
        buildNotificationItem(
          "Maintenance",
          `${ticket.priority} ticket`,
          `${ticket.issue} - ${ticket.property}`,
          "/maintenance"
        )
      );
    });

    (leaseResult.data || []).forEach((tenant) => {
      nextItems.push(
        buildNotificationItem(
          "Lease",
          "Lease ending soon",
          `${tenant.full_name} - ${tenant.property_name} - ends ${tenant.lease_end}`,
          "/tenants"
        )
      );
    });

    setItems(nextItems.slice(0, 12));
    setLoading(false);
  };

  const unreadCount = items.length;

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
        setIsOpen(true);
        if (items.length === 0 && !loading) {
          fetchNotifications();
        }
      }}
      onMouseLeave={() => {
        closeTimeoutRef.current = setTimeout(() => {
          setIsOpen(false);
        }, 180);
      }}
    >
      <button
        type="button"
        title="Notifications"
        className={`relative inline-flex h-10 w-10 items-center justify-center rounded-xl border transition ${
          isDark
            ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        }`}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-semibold text-white">
            {unreadCount}
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <div
          onMouseEnter={() => {
            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
            }
            setIsOpen(true);
          }}
          onMouseLeave={() => {
            closeTimeoutRef.current = setTimeout(() => {
              setIsOpen(false);
            }, 180);
          }}
          className={`fixed right-6 top-24 z-[9999] w-80 rounded-2xl border p-3 shadow-xl ${
            isDark
              ? "border-slate-700 bg-slate-900 text-white"
              : "border-slate-200 bg-white text-slate-900"
          }`}
        >
          <div className="flex items-center justify-between px-1 pb-3">
            <div>
              <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                Notifications
              </p>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Live portfolio alerts
              </p>
            </div>
            <button
              type="button"
              onClick={fetchNotifications}
              className="text-xs font-semibold text-sky-600"
            >
              Refresh
            </button>
          </div>

          <div className="max-h-96 space-y-2 overflow-y-auto">
            {loading ? (
              <div className={`rounded-xl p-4 text-sm ${isDark ? "bg-slate-950 text-slate-300" : "bg-slate-50 text-slate-600"}`}>
                Checking alerts...
              </div>
            ) : items.length === 0 ? (
              <div className={`rounded-xl p-4 text-sm ${isDark ? "bg-slate-950 text-slate-300" : "bg-slate-50 text-slate-600"}`}>
                No urgent notifications right now.
              </div>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    navigate(item.path);
                  }}
                  className={`w-full rounded-xl border p-3 text-left transition ${
                    isDark
                      ? "border-slate-800 bg-slate-950 hover:bg-slate-800"
                      : "border-slate-200 bg-slate-50 hover:bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item.title}
                    </p>
                    <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                      {item.type}
                    </span>
                  </div>
                  <p className={`mt-1 text-xs leading-5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {item.detail}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Sidebar({ isDark, className = "", mobile = false, onNavigate = () => {} }) {
  const { profile } = useAuth();
  const location = useLocation();
  const isTenant = String(profile?.role || "").trim().toLowerCase() === "tenant";
  const hoverCloseTimeoutRef = useRef(null);
  const [openGroups, setOpenGroups] = useState(() => {
    const defaults = {};
    navGroups.forEach((group) => {
      defaults[group.label] = group.items.some((item) => item.path === location.pathname);
    });
    return defaults;
  });
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const visibleTopNavItems = useMemo(
    () => topNavItems.filter((item) => canAccessPath(profile?.role, item.path)),
    [profile?.role]
  );
  const visibleNavGroups = useMemo(
    () =>
      navGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => canAccessPath(profile?.role, item.path)),
        }))
        .filter((group) => group.items.length > 0),
    [profile?.role]
  );
  const visibleLowerNavItems = useMemo(
    () => lowerNavItems.filter((item) => canAccessPath(profile?.role, item.path)),
    [profile?.role]
  );

  useEffect(() => {
    setOpenGroups((current) => {
      const next = { ...current };
      visibleNavGroups.forEach((group) => {
        next[group.label] = group.items.some((item) => item.path === location.pathname);
      });
      return next;
    });
  }, [location.pathname, visibleNavGroups]);

  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
      }
    };
  }, []);

  return (
    <aside
      className={`${
        mobile
          ? "flex h-full w-[min(88vw,22rem)] flex-col rounded-r-[2rem] border-r p-5 shadow-xl"
          : "hidden xl:sticky xl:top-8 xl:flex xl:h-[calc(100vh-4rem)] xl:w-72 xl:flex-col xl:self-start xl:overflow-y-auto xl:rounded-[2rem] xl:border xl:p-5 xl:shadow-sm"
      } ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"} ${className}`}
    >
      <div className="mb-6 px-2">
        <div
          className={`flex items-center justify-center rounded-3xl border px-6 py-6 shadow-sm transition-all hover:shadow-md ${
            isDark
              ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
              : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
          }`}
        >
          <img
            src={logo}
            alt="SpaceM8"
            className="h-20 w-auto cursor-pointer object-contain transition-all duration-300 hover:scale-110"
            onClick={() => (window.location.href = "/")}
          />
        </div>

        <p
          className={`mt-4 text-center text-sm font-medium ${
            isDark ? "text-slate-300" : "text-slate-600"
          }`}
        >
          Where Property Meets Intelligence
        </p>
      </div>

      <nav className="mt-1 space-y-2">
        {isTenant ? (
          <>
            {tenantPortalItems.map((item) => {
              const Icon = item.icon;

              return (
                <NavLink
                  key={item.label}
                  to={item.path}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all ${
                      isActive
                        ? isDark
                          ? "bg-slate-100 text-slate-950"
                          : "bg-sky-50 text-slate-900 ring-1 ring-sky-100"
                        : isDark
                        ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                        : "text-slate-600 hover:bg-gradient-to-br hover:from-white hover:to-sky-50 hover:text-slate-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={`h-5 w-5 ${
                          isActive ? "text-sky-700" : isDark ? "text-slate-500" : "text-slate-400"
                        }`}
                      />
                      <span className="text-sm font-medium">{item.label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}

            <NavLink
              to="/settings"
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-2xl px-4 py-3 transition ${
                  isActive
                    ? isDark
                      ? "bg-slate-100 text-slate-950"
                      : "bg-white text-slate-900 shadow-sm ring-1 ring-sky-100"
                    : isDark
                    ? "text-slate-300 hover:bg-slate-900"
                    : "text-slate-700 hover:bg-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Settings
                    className={`h-5 w-5 ${
                      isActive ? "text-sky-700" : isDark ? "text-slate-500" : "text-slate-400"
                    }`}
                  />
                  <span className="text-sm font-medium">Settings</span>
                </>
              )}
            </NavLink>
          </>
        ) : (
          <>
            {visibleTopNavItems.map((item) => {
              const Icon = item.icon;

              return (
                <NavLink
                  key={item.label}
                  to={item.path}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all ${
                      isActive
                      ? isDark
                        ? "bg-slate-100 text-slate-950"
                        : "bg-sky-50 text-slate-900 ring-1 ring-sky-100"
                      : isDark
                      ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                      : "text-slate-600 hover:bg-gradient-to-br hover:from-white hover:to-sky-50 hover:text-slate-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={`h-5 w-5 ${
                          isActive ? "text-sky-700" : isDark ? "text-slate-500" : "text-slate-400"
                        }`}
                      />
                      <span className="text-sm font-medium">{item.label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}

            {visibleNavGroups.map((group) => {
          const GroupIcon = group.icon;
          const hasActiveChild = group.items.some((item) => item.path === location.pathname);
          const isOpen = mobile
            ? Boolean(openGroups[group.label])
            : hoveredGroup === group.label || hasActiveChild;

          return (
            <div
              key={group.label}
              onMouseEnter={() => {
                if (!mobile) {
                  if (hoverCloseTimeoutRef.current) {
                    clearTimeout(hoverCloseTimeoutRef.current);
                  }
                  setHoveredGroup(group.label);
                }
              }}
              onMouseLeave={() => {
                if (!mobile) {
                  hoverCloseTimeoutRef.current = setTimeout(() => {
                    setHoveredGroup(null);
                  }, 140);
                }
              }}
              className={`rounded-2xl border transition ${
                isOpen
                  ? isDark
                    ? "border-slate-700 bg-slate-950"
                    : "border-sky-200 bg-sky-50/80"
                  : isDark
                  ? "border-slate-800 bg-slate-950/70"
                  : "border-slate-200 bg-slate-50/80"
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  if (mobile) {
                    setOpenGroups((current) => {
                      const next = {};
                      visibleNavGroups.forEach((itemGroup) => {
                        next[itemGroup.label] =
                          itemGroup.label === group.label ? !current[group.label] : false;
                      });
                      return next;
                    });
                  }
                }}
                className={`flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left transition ${
                  isOpen
                    ? isDark
                      ? "text-white"
                      : "text-slate-900"
                    : isDark
                    ? "text-slate-300 hover:bg-slate-900"
                    : "text-slate-700"
                }`}
              >
                <span className="flex items-center gap-3">
                  <GroupIcon className={`h-5 w-5 ${hasActiveChild ? "text-sky-600" : isDark ? "text-slate-500" : "text-slate-400"}`} />
                  <span className="text-sm font-semibold">{group.label}</span>
                </span>
                <ChevronRight
                  className={`h-4 w-4 transition-transform duration-300 ${
                    isOpen ? "rotate-90" : ""
                  } ${isDark ? "text-slate-500" : "text-slate-400"}`}
                />
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-out ${
                  isOpen ? "max-h-[26rem] pb-3 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="space-y-1 px-2">
                  {group.items.map((item) => {
                    const Icon = item.icon;

                    return (
                      <NavLink
                        key={item.label}
                        to={item.path}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          `flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-all ${
                            isActive
                              ? isDark
                                ? "bg-slate-100 text-slate-950"
                                : "bg-white text-slate-900 ring-1 ring-sky-100"
                              : isDark
                              ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                              : "text-slate-600 hover:bg-white hover:text-slate-900"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <Icon
                              className={`h-4 w-4 ${
                                isActive
                                  ? "text-sky-700"
                                  : isDark
                                  ? "text-slate-500"
                                  : "text-slate-400"
                              }`}
                            />
                            <span className="text-sm font-medium">{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}

            {visibleLowerNavItems.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.label}
              to={item.path}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-2xl px-4 py-3 transition ${
                  isActive
                    ? isDark
                      ? "bg-slate-100 text-slate-950"
                      : "bg-white text-slate-900 shadow-sm ring-1 ring-sky-100"
                    : isDark
                    ? "text-slate-300 hover:bg-slate-900"
                    : "text-slate-700 hover:bg-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`h-5 w-5 ${
                      isActive ? "text-sky-700" : isDark ? "text-slate-500" : "text-slate-400"
                    }`}
                  />
                  <span className="text-sm font-medium">{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
          </>
        )}
      </nav>

      <div
        className={`mt-6 rounded-3xl border p-5 ${
          isDark
            ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
            : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
        }`}
      >
        <p
          className={`text-xs font-semibold uppercase tracking-[0.2em] ${
            isDark ? "text-slate-400" : "text-slate-600"
          }`}
        >
          {isTenant ? "Resident Summary" : "Executive Summary"}
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              Platform Status
            </p>
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Active
            </p>
          </div>

          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              Theme Mode
            </p>
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {isDark ? "Dark" : "Light"}
            </p>
          </div>

          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              {isTenant ? "Portal Access" : "AI Layer"}
            </p>
            <p className="text-lg font-semibold text-sky-600">{isTenant ? "Resident only" : "Enabled"}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

function AIAssistantShortcut({ isDark, isOpen, onToggle, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const shortcutIconSrc = "/spacem8-ai-shortcut.png";
  const role = String(profile?.role || "").trim().toLowerCase();

  if (location.pathname === "/ai-assistant" || role === "tenant") {
    return null;
  }

  return (
    <>
      {isOpen ? (
        <div className="fixed inset-0 z-[9996]" onClick={onClose}>
          <div className="absolute inset-0 bg-slate-950/20 backdrop-blur-[1px]" />
        </div>
      ) : null}

      <div
        className={`fixed bottom-6 right-6 z-[9997] w-[min(92vw,24rem)] transition-all md:bottom-8 md:right-8 ${
          isOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-100"
        }`}
      >
        {isOpen ? (
          <div
            className={`mb-4 rounded-[2rem] border p-4 shadow-2xl transition-all duration-300 ${
              isDark ? "border-slate-700 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">
                  AI Shortcut
                </p>
                <h3 className="mt-2 text-lg font-semibold">Quick Assistant Panel</h3>
                <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Jump into the AI assistant fast from any page.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border transition ${
                  isDark ? "border-slate-700 bg-slate-900 hover:bg-slate-800" : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 space-y-2">
              {aiShortcutPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => {
                    navigate("/ai-assistant", { state: { quickPrompt: prompt } });
                    onClose();
                  }}
                  className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition-all duration-200 ${
                    isDark
                      ? "border-slate-800 bg-gradient-to-br from-slate-950 to-slate-900 hover:border-slate-700 hover:bg-slate-800"
                      : "border-slate-200 bg-gradient-to-br from-white to-sky-50 hover:border-sky-200 hover:bg-white"
                  }`}
                >
                  {prompt}
                </button>
              ))}
            </div>

            <div
              className={`mt-4 rounded-3xl border p-3 text-xs leading-5 transition-all duration-200 ${
                isDark
                  ? "border-slate-800 bg-slate-950 text-slate-300"
                  : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              Quick answers, drafts, and portfolio guidance are available from here without losing your place in the app.
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  navigate("/ai-assistant");
                  onClose();
                }}
                className="inline-flex flex-1 items-center justify-center rounded-2xl bg-orange-500 px-4 py-3 text-sm font-medium text-white transition hover:bg-orange-600"
              >
                <BrainCircuit className="mr-2 h-4 w-4" />
                Open Full Assistant
              </button>
            </div>
          </div>
        ) : null}

        <button
          type="button"
          onClick={onToggle}
          title="Open AI Assistant"
          className={`inline-flex items-center gap-3 rounded-full border px-4 py-3 shadow-xl transition hover:-translate-y-0.5 ${
            isDark
              ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
              : "border-slate-200 bg-white text-slate-900 hover:bg-sky-50"
          }`}
        >
          <span
            className={`flex h-11 w-16 items-center justify-center overflow-hidden rounded-full px-2 shadow-sm ${
              isDark ? "bg-slate-950/90" : "bg-white"
            }`}
          >
            <img
              src={shortcutIconSrc}
              alt="SpaceM8 AI Shortcut"
              className="h-8 w-full object-contain"
            />
          </span>
          <span className="hidden min-w-0 md:block">
            <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">
              AI Shortcut
            </span>
            <span className="block text-sm font-medium">{isOpen ? "Hide Assistant" : "Open Assistant"}</span>
          </span>
        </button>
      </div>
    </>
  );
}

export default function AppLayout({
  isDark,
  setIsDark,
  currency = "KES",
  setCurrency = () => {},
  eyebrow,
  title,
  description,
  actions,
  overlayActive = false,
  children,
}) {
  const { profile } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const role = String(profile?.role || "").trim().toLowerCase();
  const isTenant = role === "tenant";

  return (
    <div
      className={`min-h-screen ${
        isDark
          ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white"
          : "bg-gradient-to-br from-[#f8fbff] via-[#eef6ff] to-[#f3f9ff] text-slate-900"
      }`}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute inset-0 ${
            isDark
              ? "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.08),_transparent_22%),linear-gradient(to_bottom_right,_#020617,_#0f172a,_#020617)]"
              : "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.10),_transparent_24%),linear-gradient(to_bottom_right,_#f8fbff,_#f6f9fc,_#eef5fb)]"
          }`}
        />
      </div>

      {mobileNavOpen ? (
        <div className="fixed inset-0 z-[10000] xl:hidden">
          <button
            type="button"
            aria-label="Close navigation menu"
            className="absolute inset-0 bg-slate-950/50 backdrop-blur-[1px]"
            onClick={() => setMobileNavOpen(false)}
          />

          <div className="relative h-full">
            <Sidebar
              isDark={isDark}
              mobile
              className="relative z-[10001]"
              onNavigate={() => setMobileNavOpen(false)}
            />

            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close navigation menu"
              className={`absolute left-[min(88vw,22rem)] top-4 z-[10002] ml-3 inline-flex h-10 w-10 items-center justify-center rounded-xl border transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      ) : null}

      <div className="relative mx-auto flex max-w-[1600px] gap-6 p-4 md:p-6 xl:p-8">
        <Sidebar isDark={isDark} />

        <main className="min-w-0 flex-1 space-y-6 pb-72 md:pb-80 xl:pb-72">
          <section
            className={`sticky top-4 z-[9996] rounded-[2rem] border p-5 shadow-sm backdrop-blur transition-all duration-200 ${
              overlayActive
                ? "pointer-events-none opacity-15 saturate-50"
                : ""
            } ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="grid gap-5 xl:grid-cols-[minmax(320px,1fr)_auto] xl:items-start">
              <div className="min-w-0">
                {eyebrow ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                    {eyebrow}
                  </p>
                ) : null}

                {title ? (
                  <h1
                    className={`mt-2 text-3xl font-semibold tracking-tight ${
                      isDark ? "text-white" : "text-slate-900"
                    }`}
                  >
                    {title}
                  </h1>
                ) : null}

                {description ? (
                  <p
                    className={`mt-2 max-w-xl text-sm leading-6 ${
                      isDark ? "text-slate-300" : "text-slate-600"
                    }`}
                  >
                    {description}
                  </p>
                ) : null}
              </div>

              <div className="w-full xl:w-auto">
                <div className="flex flex-wrap items-center justify-start gap-2 xl:flex-nowrap xl:justify-end">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <TopButton
                      onClick={() => setMobileNavOpen(true)}
                      title="Open navigation menu"
                      className={`w-10 px-0 xl:hidden ${
                        isDark
                          ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <Menu className="h-4 w-4" />
                    </TopButton>

                    {/* Desktop theme button */}
                    <TopButton
                      onClick={() => setIsDark(!isDark)}
                      className={`hidden sm:inline-flex ${
                        isDark
                          ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                      {isDark ? "Light" : "Dark"}
                    </TopButton>

                    {/* Mobile icon-only theme button */}
                    <TopButton
                      onClick={() => setIsDark(!isDark)}
                      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                      className={`sm:hidden w-10 px-0 ${
                        isDark
                          ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </TopButton>

                    {/* Desktop currency button */}
                    <TopButton
                      onClick={() => setCurrency(currency === "KES" ? "USD" : "KES")}
                      className={`hidden sm:inline-flex ${
                        isDark
                          ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {currency}
                    </TopButton>

                    {/* Mobile icon-like compact currency button */}
                    <TopButton
                      onClick={() => setCurrency(currency === "KES" ? "USD" : "KES")}
                      title="Toggle currency"
                      className={`sm:hidden min-w-[48px] px-3 ${
                        isDark
                          ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span className="text-xs font-semibold">{currency}</span>
                    </TopButton>
                  </div>

                  <div className="w-[190px] md:w-[220px]">
                    {isTenant ? null : <SearchInput isDark={isDark} />}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap">
                    {actions}
                    {isTenant ? null : <NotificationsMenu isDark={isDark} />}
                    <ProfileMenu isDark={isDark} />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {children}
        </main>
      </div>

      <div className="pointer-events-none fixed bottom-4 inset-x-0 z-40 md:bottom-6">
        <div className="mx-auto max-w-[1600px] px-4 md:px-6 xl:px-8">
          <div className="xl:ml-[calc(18rem+1.5rem)]">
            <div className="pointer-events-auto">
              <Footer isDark={isDark} />
            </div>
          </div>
        </div>
      </div>

      <AIAssistantShortcut
        isDark={isDark}
        isOpen={aiPanelOpen}
        onToggle={() => setAiPanelOpen((current) => !current)}
        onClose={() => setAiPanelOpen(false)}
      />
    </div>
  );
}
